import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    ListItemText,
    Checkbox,
    IconButton,
    Popover,
    Slider,
    Autocomplete,
    TextField,
    Tooltip,
    Typography,
    Stack,
    InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import TableChartIcon from '@mui/icons-material/TableChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfDay, endOfDay, format } from 'date-fns';
import { useGetErrorsBySerialNumberQuery } from '../../redux/hardwareErrorsApi';
import { hardwareErrorColumns, hardwareErrorColumnsDetached } from '../system/hardwareErrors.DatagridColumns';
import ErrorsTimeline from '../system/CutterDetailsHistory.errorsTimeLine';
import { ErrorTimelinePoint, severityOptions, HardwareError } from '../../types/hardwareErrors';
import { CutterHistory } from '../../types/cutterHistory';



// ── Shared filter state (lifted to parent so tab ↔ dialog share the same values) ──
export interface ErrorsFilterState {
    viewMode: 'table' | 'timeline';
    severity: string[];
    errorCodeFilter: string;
    sourceFilter: string[];
    dateRangeValue: number[];   // [0,0] = not yet initialised from data
    selectedDate: Date | null;
}

export const initialErrorsFilterState: ErrorsFilterState = {
    viewMode: 'table',
    severity: severityOptions,
    errorCodeFilter: '',
    sourceFilter: [],
    dateRangeValue: [0, 0],
    selectedDate: null,
};

export interface CutterDetailsHistoryErrorsProps {
    cutter: CutterHistory;
    onDetach?: () => void;
    isDetached?: boolean;
    filterState: ErrorsFilterState;
    onFilterChange: (patch: Partial<ErrorsFilterState>) => void;
    onExportDataChange?: (data: any[], columns: Array<{ key: string; header: string }>) => void;
}

export const CutterDetailsHistoryErrors: React.FC<CutterDetailsHistoryErrorsProps> = ({ cutter, onDetach, isDetached, filterState, onFilterChange, onExportDataChange }) => {
    const { viewMode, severity, errorCodeFilter, sourceFilter, dateRangeValue, selectedDate } = filterState;
    // calendarAnchor is strictly local UI state — each view has its own Popover anchor
    const [calendarAnchor, setCalendarAnchor] = useState<HTMLButtonElement | null>(null);

    // Fetch ALL errors data (no pagination on server)
    const { data: errorsData, isLoading: isLoadingErrors }: ReturnType<typeof useGetErrorsBySerialNumberQuery> = useGetErrorsBySerialNumberQuery({
        serialNumber: cutter.serialNumber,
        severity: severity || undefined
    });


    // Calculate date range from actual error data
    const dateRange = useMemo(() => {
        if (!errorsData?.errors || errorsData.errors.length === 0) return { min: 0, max: 0 };
        const dates = errorsData.errors.map((e: { errorDate: string }) => new Date(e.errorDate).getTime());
        return { min: Math.min(...dates), max: Math.max(...dates) };
    }, [errorsData]);

    // Auto-initialise slider to the full data range on first load.
    // dateRangeValue[0]===0 guards against resetting after the user has moved the slider.
    useEffect(() => {
        if (dateRange.min > 0 && dateRange.max > 0 && dateRangeValue[0] === 0) {
            onFilterChange({ dateRangeValue: [dateRange.min, dateRange.max] });
        }
    }, [dateRange, dateRangeValue, onFilterChange]);

    const handleDateRangeChange = (_event: Event, newValue: number | number[]): void => {
        onFilterChange({ dateRangeValue: newValue as number[], selectedDate: null });
    };

    const handleCalendarDateSelect = (date: Date | null): void => {
        if (date && dateRange.min > 0) {
            const start = Math.max(startOfDay(date).getTime(), dateRange.min);
            const end = Math.min(endOfDay(date).getTime(), dateRange.max);
            onFilterChange({ selectedDate: date, dateRangeValue: start <= end ? [start, end] : dateRangeValue });
        } else {
            onFilterChange({ selectedDate: null, dateRangeValue: [dateRange.min, dateRange.max] });
        }
        // Keep calendar open — user dismisses it by clicking outside
    };

    const formatDateLabel = (value: number): string => {
        return new Date(value).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Unique error codes for autocomplete (sorted, deduplicated)
    const uniqueErrorCodes = useMemo<string[]>(() => {
        if (!errorsData?.errors) return [];
        return Array.from<string>(new Set(errorsData.errors.map((e: { errorCode: string }) => e.errorCode))).sort((a, b) => a.localeCompare(b));
    }, [errorsData]);

    // Unique error sources for source filter
    const uniqueErrorSources = useMemo<string[]>(() => {
        if (!errorsData?.errors) return [];
        return Array.from<string>(new Set(errorsData.errors.map((e: HardwareError) => e.errorSource ?? 'UNKNOWN'))).sort((a, b) => a.localeCompare(b));
    }, [errorsData]);

    // Client-side filtering: date range + error code + source
    const filteredErrors = useMemo(() => {
        if (!errorsData?.errors) return [];
        return errorsData.errors.filter((error: HardwareError) => {
            const t = new Date(error.errorDate).getTime();
            return (
                t >= dateRangeValue[0] && t <= dateRangeValue[1] &&
                (!errorCodeFilter || error.errorCode.toLowerCase().includes(errorCodeFilter.toLowerCase())) &&
                (sourceFilter.length === 0 || sourceFilter.includes(error.errorSource ?? 'UNKNOWN'))
            );
        });
    }, [errorsData, dateRangeValue, errorCodeFilter, sourceFilter]);

    // Set of 'yyyy-MM-dd' strings for calendar day-disabling
    const daysWithErrors = useMemo(() => {
        if (!errorsData?.errors) return new Set<string>();
        return new Set(errorsData.errors.map((e: { errorDate: string }) => format(new Date(e.errorDate), 'yyyy-MM-dd')));
    }, [errorsData]);

    // Lift filtered errors + columns to parent for TabsWithExport
    useEffect(() => {
        if (onExportDataChange) {
            const cols = filteredErrors.length > 0
                ? Object.keys(filteredErrors[0]).map(k => ({ key: k, header: k }))
                : [];
            onExportDataChange(filteredErrors, cols);
        }
    }, [filteredErrors, onExportDataChange]);

    // Sorted day list for prev/next navigation (derived directly to avoid Set→Array TS issues)
    const sortedErrorDays = useMemo<string[]>(() => {
        if (!errorsData?.errors) return [];
        return Array.from<string>(new Set(errorsData.errors.map((e: { errorDate: string }) => format(new Date(e.errorDate), 'yyyy-MM-dd')))).sort((a, b) => a.localeCompare(b));
    }, [errorsData]);

    const navigateDay = (direction: 'prev' | 'next'): void => {
        const currentKey = selectedDate
            ? format(selectedDate, 'yyyy-MM-dd')
            : format(new Date(dateRangeValue[0] || Date.now()), 'yyyy-MM-dd');
        const target = direction === 'prev'
            ? [...sortedErrorDays].reverse().find(d => d < currentKey)
            : sortedErrorDays.find(d => d > currentKey);
        if (target) handleCalendarDateSelect(new Date(target + 'T12:00:00'));
    };

    const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newValue: 'table' | 'timeline' | null): void => {
        if (newValue !== null) onFilterChange({ viewMode: newValue });
    };

    const handleSeverityChange = (event: SelectChangeEvent<string[]>): void => {
        onFilterChange({ severity: typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value });
    };

    const handleTimelinePointClick = (dataPoint: ErrorTimelinePoint): void => {
        const clickedDate = new Date(dataPoint.date).getTime();
        onFilterChange({ sourceFilter: [dataPoint.source], dateRangeValue: [clickedDate, dateRangeValue[1]], viewMode: 'table' });
    };

    const handleBrushChange = (range: [number, number] | null): void => {
        onFilterChange({ dateRangeValue: range ?? [dateRange.min, dateRange.max] });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction='column' sx={{ p: 1, width: '100%', height: '100%', minWidth: 0, overflow: 'hidden' }}>
                {/* Controls */}
                <Stack direction="row" gap={1} mb={0.5} flexWrap="nowrap" alignItems="center">
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                        sx={{ '& .MuiToggleButton-root': { px: 0.75, py: 0.5, minWidth: 36 } }}
                    >


                        <ToggleButton value="table">
                            <TableChartIcon sx={{ fontSize: 18 }} />
                        </ToggleButton>
                        <ToggleButton value="timeline">
                            <TimelineIcon sx={{ fontSize: 18 }} />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel>Source</InputLabel>
                        <Select<string[]> value={sourceFilter} label="Source"
                            multiple
                            onChange={(event: SelectChangeEvent<string[]>) => {
                                onFilterChange({ sourceFilter: typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value });
                            }}
                            endAdornment={
                                sourceFilter.length > 0 ? (
                                    <InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}>
                                        <IconButton size="small" onClick={() => onFilterChange({ sourceFilter: [] })}>
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined
                            }
                            renderValue={(selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ')}
                            sx={{ fontSize: '0.875rem' }}>
                            {uniqueErrorSources.map((src) => (
                                <MenuItem key={src} value={src}>
                                    <Checkbox checked={sourceFilter.includes(src)} />
                                    <ListItemText primary={src} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Autocomplete
                        size="small"
                        options={uniqueErrorCodes}
                        freeSolo
                        inputValue={errorCodeFilter}
                        onInputChange={(_event: React.SyntheticEvent, newInputValue: string) => onFilterChange({ errorCodeFilter: newInputValue })}
                        renderInput={(params) => (
                            <TextField {...params} label="Error Code"
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {errorCodeFilter && (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => onFilterChange({ errorCodeFilter: '' })}>
                                                            <ClearIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }
                                }}
                            />
                        )}
                        sx={{ minWidth: 150, fontSize: '0.875rem' }}
                    />

                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel>Severity</InputLabel>
                        <Select value={severity} label="Severity"
                            multiple
                            onChange={handleSeverityChange}
                            endAdornment={
                                severity.length < severityOptions.length ? (
                                    <InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}>
                                        <IconButton size="small" onClick={() => onFilterChange({ severity: severityOptions })}>
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined
                            }
                            renderValue={(selected) => selected.join(', ')}
                            sx={{ fontSize: '0.875rem' }}>
                            {severityOptions.map((sev) => (
                                <MenuItem key={sev} value={sev}>
                                    <Checkbox checked={severity.includes(sev)} />
                                    <ListItemText primary={sev} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Clear all filters */}
                    {(sourceFilter.length > 0 || errorCodeFilter !== '' || severity.length < severityOptions.length ||
                        selectedDate !== null || dateRangeValue[0] !== dateRange.min || dateRangeValue[1] !== dateRange.max) && (
                        <Tooltip title="Reset all filters to default">
                            <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onFilterChange({
                                    sourceFilter: [],
                                    errorCodeFilter: '',
                                    severity: severityOptions,
                                    selectedDate: null,
                                    dateRangeValue: [dateRange.min, dateRange.max]
                                })}
                                sx={{ flexShrink: 0, py: 0.25, lineHeight: 1, whiteSpace: 'nowrap' }}
                            >
                                Reset filters
                            </Button>
                        </Tooltip>
                    )}

                </Stack>

                {/* Date Range Slider */}
                <Stack direction="row" alignItems="center" sx={{ display: 'flex', justifyContent: 'space-between', mb: '0.5em' }}>

                    {/* Prev day */}
                    <Tooltip title="Previous day with errors">
                        <span>
                            <IconButton size="small" onClick={() => navigateDay('prev')}
                                disabled={sortedErrorDays.length === 0} sx={{ flexShrink: 0, p: 0.25 }}>
                                <ChevronLeftIcon sx={{ fontSize: 16, fontStyle: 'weight' }} />
                            </IconButton>
                        </span>
                    </Tooltip>

                    {/* Calendar picker toggle */}
                    <Tooltip title={selectedDate ? `Showing: ${format(selectedDate, 'dd MMM yyyy')} — click to change` : 'Filter by specific date'}>
                        <IconButton
                            size="small"
                            color={selectedDate ? 'primary' : 'secondary'}
                            onClick={(e) => setCalendarAnchor(e.currentTarget)}
                            sx={{ flexShrink: 0, p: 0.25 }}
                        >
                            <CalendarMonthIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>

                    {/* Next day */}
                    <Tooltip title="Next day with errors">
                        <span>
                            <IconButton size="small" onClick={() => navigateDay('next')}
                                disabled={sortedErrorDays.length === 0} sx={{ flexShrink: 0, p: 0.25 }}>
                                <ChevronRightIcon sx={{ fontSize: 16, fontStyle: 'weight' }} />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Popover
                        open={Boolean(calendarAnchor)}
                        anchorEl={calendarAnchor}
                        onClose={() => setCalendarAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{ paper: { sx: { border: 1, borderColor: 'divider', borderRadius: 2 } } }}
                    >
                        <DateCalendar
                            value={selectedDate}
                            onChange={handleCalendarDateSelect}
                            minDate={dateRange.min > 0 ? new Date(dateRange.min) : undefined}
                            maxDate={dateRange.max > 0 ? new Date(dateRange.max) : undefined}
                            shouldDisableDate={(date) => !daysWithErrors.has(format(date, 'yyyy-MM-dd'))}
                        />
                        {selectedDate && (
                            <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => { handleCalendarDateSelect(null); setCalendarAnchor(null); }}>Clear filter</Button>
                            </Box>
                        )}
                    </Popover>

                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{formatDateLabel(dateRangeValue[0])}</Typography>
                    <Slider sx={{ marginX: '1em' }}
                        value={dateRangeValue}
                        onChange={handleDateRangeChange}
                        min={dateRange.min || 0}
                        max={dateRange.max || 1}
                        valueLabelDisplay="off" />
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{formatDateLabel(dateRangeValue[1])}</Typography>
                </Stack>

                {/* Content Area */}
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    {viewMode === 'table' ? (
                        <DataGrid
                            autoHeight
                            sx={{ display: "flex", flexDirection: "column-reverse" }} // Get the pagination on top of the table ?
                            rows={filteredErrors}
                            columns={isDetached ? hardwareErrorColumnsDetached : hardwareErrorColumns}
                            loading={isLoadingErrors}
                            density='compact'
                            pagination
                            initialState={{
                                pagination: { paginationModel: { pageSize: isDetached ? 25 : 10 } }
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            disableRowSelectionOnClick
                            getRowId={(row) => `${row.serialNumber}-${row.errorCode}-${row.errorDate}`}
                        />
                    ) :
                        (filteredErrors.length > 0 ? (
                            <ErrorsTimeline
                                errorsData={{ ...errorsData, errors: filteredErrors }}
                                onDataPointClick={handleTimelinePointClick}
                                onBrushChange={handleBrushChange}
                                fillHeight={isDetached}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No timeline data available for the selected filters
                                </Typography>
                            </Box>
                        ))
                    }
                </Box>
            </Stack>
        </LocalizationProvider>
    );
};

export default CutterDetailsHistoryErrors;
